import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import {
  courseQueryKey,
  type CourseHomepage,
  type RawComponent,
} from './useCourse';

export type CreateCourseComponentKind = 'divider' | 'kai_coach' | 'section';

export interface CreateCourseComponentInput {
  kind: CreateCourseComponentKind;
  title: string;
}

export interface CreateCourseComponentResult {
  component: RawComponent | null;
  componentId?: string;
  kind: CreateCourseComponentKind;
}

interface CreateCoursePayload {
  components: Array<Record<string, unknown>>;
  id: string;
}

interface RawUpdateCourseCreateResponse {
  component?: RawComponent | null;
  components?: RawComponent[] | null;
  course?: {
    components?: RawComponent[] | null;
  } | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function getCreatedComponentId(component: RawComponent | null | undefined) {
  return normalizeString(component?.uid) ?? normalizeString(component?.id);
}

function pickCreatedComponent(
  components: RawComponent[],
  title?: string,
) {
  if (components.length === 0) {
    return null;
  }

  const normalizedTitle = title?.trim();

  if (!normalizedTitle) {
    return components[0] ?? null;
  }

  const matchingComponents = components.filter(
    (component) => normalizeString(component.title) === normalizedTitle,
  );

  if (matchingComponents.length === 1) {
    return matchingComponents[0] ?? null;
  }

  return components[0] ?? null;
}

export function unwrapCreatedComponent(
  response:
    | RawUpdateCourseCreateResponse
    | RawComponent
    | LegacyKuduResponseEnvelope<RawUpdateCourseCreateResponse | RawComponent>
    | null
    | undefined,
  title?: string,
): RawComponent | null {
  if (!response || !isRecord(response)) {
    return null;
  }

  if ('Response' in response) {
    return unwrapCreatedComponent(
      response.Response as RawUpdateCourseCreateResponse | RawComponent | null,
      title,
    );
  }

  if ('component' in response && isRecord(response.component)) {
    return response.component as RawComponent;
  }

  if ('components' in response && Array.isArray(response.components)) {
    return pickCreatedComponent(
      response.components.filter(isRecord) as RawComponent[],
      title,
    );
  }

  if ('course' in response && isRecord(response.course)) {
    const courseComponents = Array.isArray(response.course.components)
      ? (response.course.components.filter(isRecord) as RawComponent[])
      : [];
    return pickCreatedComponent(courseComponents, title);
  }

  if ('title' in response || 'id' in response || 'uid' in response) {
    return response as RawComponent;
  }

  return null;
}

export function buildNewDividerPayload(
  courseId: string,
  title: string,
): CreateCoursePayload {
  const normalizedTitle = title.trim();

  return {
    components: [
      {
        id: normalizedTitle,
        is_live: false,
        is_section_divider: true,
        title: normalizedTitle,
      },
    ],
    id: courseId,
  };
}

export function buildNewSectionPayload(
  courseId: string,
  title: string,
): CreateCoursePayload {
  const normalizedTitle = title.trim();
  const sectionId = `${courseId}${normalizedTitle}q`;

  return {
    components: [
      {
        id: normalizedTitle,
        is_live: false,
        is_section_divider: false,
        pre_lecture_question_settings: {},
        question_settings: {},
        sections: [
          {
            id: sectionId,
            question_section: {
              id: sectionId,
            },
          },
        ],
        title: normalizedTitle,
      },
    ],
    id: courseId,
  };
}

export function buildNewKaiCoachPayload(
  courseId: string,
  title: string,
): CreateCoursePayload {
  const normalizedTitle = title.trim();
  const sectionId = `${courseId}${normalizedTitle}coach`;

  return {
    components: [
      {
        id: normalizedTitle,
        is_live: false,
        is_section_divider: false,
        sections: [
          {
            id: sectionId,
            kai_coach_section: {
              activity_dimension_label: 'Activity',
              sessions: [],
              session_dimension_label: 'Session',
              standing_instructions: [],
            },
          },
        ],
        title: normalizedTitle,
      },
    ],
    id: courseId,
  };
}

export function buildComponentCreationPayload({
  courseId,
  kind,
  title,
}: {
  courseId: string;
  kind: CreateCourseComponentKind;
  title: string;
}) {
  if (kind === 'divider') {
    return buildNewDividerPayload(courseId, title);
  }
  if (kind === 'kai_coach') {
    return buildNewKaiCoachPayload(courseId, title);
  }
  return buildNewSectionPayload(courseId, title);
}

export function findCreatedComponentIdInCourseState({
  nextCourse,
  previousComponentUids,
}: {
  nextCourse: CourseHomepage | undefined;
  previousComponentUids: string[];
}) {
  if (!nextCourse) {
    return null;
  }

  const previousIds = new Set(previousComponentUids.map(String));
  return (
    nextCourse.componentUids.find((componentUid) => !previousIds.has(String(componentUid))) ??
    null
  );
}

export function useCreateCourseComponent(courseId: string | undefined) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<CreateCourseComponentResult, ApiError, CreateCourseComponentInput>({
    mutationFn: async ({ kind, title }) => {
      const normalizedTitle = title.trim();

      if (!courseId) {
        throw createDataError('Course ID is required to create course content.');
      }

      if (!user) {
        throw createDataError('You must be signed in to create course content.');
      }

      if (!normalizedTitle) {
        throw createDataError('Enter a title before creating this item.');
      }

      const previousCourse = queryClient.getQueryData<CourseHomepage>(
        courseQueryKey(courseId),
      );
      const previousComponentUids = previousCourse?.componentUids.map(String) ?? [];
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const response = await client.invokeKuduMethod<
        | RawUpdateCourseCreateResponse
        | LegacyKuduResponseEnvelope<RawUpdateCourseCreateResponse>,
        CreateCoursePayload
      >(
        'UpdateCourse',
        buildComponentCreationPayload({
          courseId,
          kind,
          title: normalizedTitle,
        }),
      );

      await queryClient.invalidateQueries({
        queryKey: courseQueryKey(courseId),
      });
      await queryClient.refetchQueries({
        queryKey: courseQueryKey(courseId),
        type: 'active',
      });

      const component = unwrapCreatedComponent(response, normalizedTitle);
      const nextCourse = queryClient.getQueryData<CourseHomepage>(courseQueryKey(courseId));
      const courseStateComponentId = findCreatedComponentIdInCourseState({
        nextCourse,
        previousComponentUids,
      });
      const componentId = courseStateComponentId ?? undefined;

      if ((kind === 'section' || kind === 'kai_coach') && !componentId) {
        throw createDataError(
          "The item was created, but we couldn't determine its backend ID for navigation. Refresh the page and open it from the course list.",
        );
      }

      return {
        component,
        componentId,
        kind,
      };
    },
  });
}
